<template>
  <!-- Kit sheet. 700 px and up: a centred modal sheet (MacMakePack: 14
       radius, a 52 px header with a note on the right, a 60 px footer with
       a note on the left and the actions on the right). Under 700 px: a
       bottom sheet with a grab handle, a 22/700 title and a round close
       button, as the phone boards (LoopLicense, SessionMenu) draw sheets. -->
  <v-dialog
    v-model="open"
    :max-width="width"
    :persistent="persistent"
    class="sheet-modal"
    :class="{ 'sheet-modal--top': top !== null }"
    :style="top !== null ? { '--sheet-top': top + 'px' } : null"
  >
    <div class="sheet-card" role="dialog" :aria-label="title">
      <div class="sheet-handle" aria-hidden="true" />
      <header class="sheet-head">
        <span class="sheet-title">{{ title }}</span>
        <span class="sheet-spacer" />
        <span v-if="note" class="sheet-note">{{ note }}</span>
        <button
          v-if="!persistent"
          type="button"
          class="sheet-close"
          aria-label="Close"
          @click="open = false"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18" /></svg>
        </button>
      </header>
      <div class="sheet-body">
        <slot />
      </div>
      <footer v-if="$slots.actions" class="sheet-foot">
        <span v-if="$slots['footer-note']" class="sheet-foot-note"><slot name="footer-note" /></span>
        <span class="sheet-spacer" />
        <div class="sheet-actions"><slot name="actions" /></div>
      </footer>
    </div>
  </v-dialog>
</template>

<script>
export default {
  name: "SheetModal",
  props: {
    modelValue: { type: Boolean, default: false },
    title: { type: String, required: true },
    // Small text on the header's right ("Draft saved").
    note: { type: String, default: "" },
    width: { type: Number, default: 600 },
    persistent: { type: Boolean, default: false },
    // Distance from the top at 700 px and up; null centres the sheet.
    top: { type: Number, default: null },
  },
  emits: ["update:modelValue"],
  computed: {
    open: {
      get() {
        return this.modelValue;
      },
      set(v) {
        this.$emit("update:modelValue", v);
      },
    },
  },
};
</script>

<style scoped>
.sheet-card {
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - var(--sheet-top, 24px) - 24px);
  background: var(--cl-sheet);
  border: 1px solid var(--cl-separator);
  border-radius: var(--cl-r-2xl);
  box-shadow: 0 24px 60px var(--cl-scrim);
  color: var(--cl-label);
  font-family: var(--cl-font);
  overflow: hidden;
}
.sheet-handle {
  display: none;
}
.sheet-head {
  flex-shrink: 0;
  height: 52px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 20px;
  border-bottom: 1px solid var(--border-subtle);
}
.sheet-title {
  font: var(--cl-title3);
  color: var(--cl-label);
}
.sheet-spacer {
  flex-grow: 1;
}
.sheet-note {
  font: var(--cl-subhead);
  color: var(--cl-label-50);
  white-space: nowrap;
}
/* The phone sheet's close disc; the Mac sheet closes from its footer. */
.sheet-close {
  display: none;
  flex-shrink: 0;
  width: 30px;
  height: 30px;
  border: none;
  border-radius: 50%;
  background: var(--cl-control-fill);
  color: var(--cl-label-70);
  align-items: center;
  justify-content: center;
  cursor: pointer;
}
.sheet-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 16px 20px 0;
}
.sheet-foot {
  flex-shrink: 0;
  margin-top: 16px;
  min-height: 60px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 20px;
  border-top: 1px solid var(--border-subtle);
}
.sheet-foot-note {
  font: var(--cl-subhead);
  color: var(--cl-label-55);
}
.sheet-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* Phone: a bottom sheet. */
@media (max-width: 699px) {
  .sheet-card {
    max-height: 92vh;
    border: none;
    border-radius: var(--cl-r-sheet) var(--cl-r-sheet) 0 0;
  }
  .sheet-handle {
    display: block;
    width: 36px;
    height: 5px;
    border-radius: 3px;
    background: var(--cl-handle);
    margin: 6px auto 0;
    flex-shrink: 0;
  }
  .sheet-head {
    min-height: 52px;
    height: auto;
    padding: 8px 16px 4px;
    border-bottom: none;
  }
  .sheet-title {
    font: var(--cl-title2);
  }
  .sheet-close {
    display: flex;
  }
  .sheet-body {
    padding: 4px 16px 0;
  }
  .sheet-foot {
    flex-wrap: wrap;
    border-top: none;
    padding: 0 16px 16px;
    margin-top: 12px;
  }
  .sheet-foot-note {
    flex-basis: 100%;
  }
  .sheet-foot .sheet-spacer {
    display: none;
  }
  .sheet-actions {
    flex: 1 1 100%;
  }
  .sheet-actions :slotted(.cl-btn) {
    flex: 1 1 0;
  }
}
</style>

<style>
/* The overlay teleports to <body>, so its placement can't be scoped. */
.v-overlay.sheet-modal > .v-overlay__scrim {
  background: var(--cl-scrim) !important;
  opacity: 1 !important;
}
.v-overlay.sheet-modal--top {
  align-items: flex-start;
}
.v-overlay.sheet-modal--top > .v-overlay__content {
  margin-top: var(--sheet-top, 24px);
  max-height: calc(100% - var(--sheet-top, 24px) - 24px);
}
@media (max-width: 699px) {
  .v-overlay.sheet-modal {
    align-items: flex-end;
  }
  .v-overlay.sheet-modal > .v-overlay__content {
    margin: 0 !important;
    width: 100% !important;
    max-width: 100% !important;
    max-height: 92vh;
  }
}
</style>
